import React, { createContext, forwardRef, useContext, useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import { useReactiveVar } from '@apollo/client/react';
import { Skeleton, Typography } from '@mui/material';
import type { TreeItemProps } from '@mui/x-tree-view-pro';
import { TreeItem } from '@mui/x-tree-view-pro';
import { AlineCheckbox } from 'src/components';
import { DriveIcon } from 'src/components/icons/DriveIcon';
import { FolderOutlineIcon } from 'src/components/icons/FolderOutlineIcon';
import { ImageIcon } from 'src/components/icons/ImageIcon';
import { MSExcelIcon } from 'src/components/icons/MSExcelIcon';
import { MSPPTIcon } from 'src/components/icons/MSPPTIcon';
import { MSWordIcon } from 'src/components/icons/MSWordIcon';
import { PDFIcon } from 'src/components/icons/PDFIcon';
import { TextDocumentIcon } from 'src/components/icons/TextDocumentIcon';
import { getFileFormat } from 'src/components/StorableIcon';
import { makeStyles } from 'tss-react/mui';

import { selectionStateVar } from './FileStorageSyncDialog';
import { ResourceType } from './useFolderSyncJobs';
import type { FolderChildrenPaginationState, TreeNode } from './useFolderSyncJobs';

type FolderPaginationContextValue = {
  loadingMoreParentIds: Set<string>;
  onLoadMore: (parentId: string) => Promise<void>;
  paginationByParent: Map<string, FolderChildrenPaginationState>;
  scrollRootRef: RefObject<HTMLDivElement | null>;
};

const PAGINATION_PREFETCH_ROOT_MARGIN = '0px 0px 120px';
const PAGINATION_SKELETON_WIDTHS = ['64%', '48%', '56%'];
export const FolderPaginationContext = createContext<FolderPaginationContextValue | null>(null);

export const labelGapPx = 8;
export const folderIconWidthPx = 24;
/**
 * How far each folder level steps in. Half the folder icon's footprint: a
 * full step (icon plus gap) pushed deep folders too far right for a tree that
 * already leads every row with an expand chevron and a checkbox.
 */
export const TREE_ITEM_INDENTATION_PX = 16;

type StyleProps = {
  isSelectable: boolean;
};

const useStyles = makeStyles<StyleProps>()((theme, { isSelectable }) => ({
  checkbox: {
    padding: 0,
    visibility: isSelectable ? ('visible' as const) : ('hidden' as const),
  },
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
  },
  iconWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  label: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    gap: labelGapPx,
    minWidth: 0,
    overflow: 'hidden',
  },
  labelText: {
    minWidth: 0,
  },
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
  },
}));

const usePaginationSkeletonStyles = makeStyles<{ treeDepth: number }>()((theme, { treeDepth }) => ({
  row: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(1),
    listStyle: 'none',
    minHeight: 32,
    padding: theme.spacing(0.5, 1),
    paddingLeft: `calc(${theme.spacing(1)} + ${treeDepth * TREE_ITEM_INDENTATION_PX}px)`,
    width: '100%',
  },
  text: {
    maxWidth: 240,
  },
}));

export function AlineTreeItem({ children, itemId, label }: TreeItemProps): JSX.Element | null {
  const { treeMap } = useReactiveVar(selectionStateVar);
  const pagination = useContext(FolderPaginationContext);
  const paginationTriggerRef = useRef<HTMLDivElement>(null);

  const item = treeMap.get(itemId);
  const parentId = item?.parentId;
  const parentPagination = parentId ? pagination?.paginationByParent.get(parentId) : undefined;
  const isPaginationTrigger =
    Boolean(parentPagination?.hasMore) && parentPagination?.lastItemId === itemId;
  const isLoadingMore =
    isPaginationTrigger && Boolean(parentId && pagination?.loadingMoreParentIds.has(parentId));
  const isSelectable = item?.type !== 'file';
  const treeDepth = item ? getTreeNodeDepth(item, treeMap) : 0;
  const { classes } = useStyles({ isSelectable });

  useEffect(() => {
    const trigger = paginationTriggerRef.current;
    const scrollRoot = pagination?.scrollRootRef.current;
    if (!isPaginationTrigger || !pagination || !parentId || !scrollRoot || !trigger) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) void pagination.onLoadMore(parentId);
      },
      {
        root: scrollRoot,
        rootMargin: PAGINATION_PREFETCH_ROOT_MARGIN,
      }
    );
    observer.observe(trigger);

    return () => observer.disconnect();
  }, [isPaginationTrigger, pagination, parentId]);

  if (!item) return null;

  return (
    <>
      <TreeItem
        itemId={itemId}
        label={label}
        slotProps={{
          content: {
            ref: paginationTriggerRef,
          },
        }}
        slots={{
          checkbox: forwardRef(function CheckboxSlot(
            { checked, ...rest },
            ref: React.Ref<HTMLButtonElement>
          ) {
            return (
              <AlineCheckbox
                ref={ref}
                checked={checked}
                classes={{ root: classes.checkbox }}
                onClick={e => e.stopPropagation()}
                {...rest}
              />
            );
          }),
          label: () => {
            return (
              <div className={classes.label}>
                {/* ItemIcon must be wrapped in div so icon doesn't shrink w/ large title text. */}
                <div className={classes.iconWrapper}>
                  <ItemIcon item={item} />
                </div>
                <Typography noWrap className={classes.labelText} variant="body1">
                  {label}
                </Typography>
              </div>
            );
          },
        }}
      >
        {children}
      </TreeItem>
      {isLoadingMore && <FolderPaginationSkeleton treeDepth={treeDepth} />}
    </>
  );
}

function FolderPaginationSkeleton({ treeDepth }: { treeDepth: number }): JSX.Element {
  const { classes } = usePaginationSkeletonStyles({ treeDepth });

  return (
    <>
      {PAGINATION_SKELETON_WIDTHS.map(width => (
        <li key={width} className={classes.row}>
          <Skeleton height={18} variant="rounded" width={16} />
          <Skeleton height={24} variant="rounded" width={24} />
          <Skeleton height={24} variant="circular" width={24} />
          <Skeleton className={classes.text} height={24} variant="text" width={width} />
        </li>
      ))}
    </>
  );
}

function getTreeNodeDepth(item: TreeNode, treeMap: Map<string, TreeNode>): number {
  let depth = 0;
  let parentId = item.parentId;

  while (parentId) {
    depth += 1;
    parentId = treeMap.get(parentId)?.parentId;
  }

  return depth;
}

export const folderIconHeightPx = 24;

function ItemIcon({ item }: { item: TreeNode }): JSX.Element {
  switch (item.type) {
    case ResourceType.Drive:
      return <DriveIcon height={folderIconHeightPx} width={folderIconWidthPx} />;
    case ResourceType.Folder:
      return <FolderOutlineIcon height={folderIconHeightPx} width={folderIconWidthPx} />;
    case ResourceType.File:
      return <DriveFileIcon mimeType={item.mimeType} />;
  }
}

function DriveFileIcon({ mimeType }: { mimeType?: string }) {
  // Unfortunately, Dropbox and Box do NOT return mimeType.  Ugh!
  if (!mimeType) return <TextDocumentIcon />;

  const fileFormat = getFileFormat(mimeType);

  switch (fileFormat) {
    case 'pdf':
      return <PDFIcon />;
    case 'msWord':
      return <MSWordIcon />;
    case 'msPPT':
      return <MSPPTIcon />;
    case 'msExcel':
      return <MSExcelIcon />;
    case 'image':
      return <ImageIcon />;
    case 'default':
    default:
      return <TextDocumentIcon />;
  }
}
